import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/layouts/AdminLayout";
import { Form, Table, Spinner, Card, Button } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "../../configs/app";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { makeTitleCase } from "../../utils/helpers";
import { FaHotel, FaArrowLeft, FaBed, FaCheckCircle, FaTimesCircle } from "react-icons/fa";

const fetchHotelsAdmin = async () => {
  const response = await axios.get(`${API_URL}/hotels/admin/all`);
  return response.data;
};

const fetchRoomsByHotelAdmin = async ({ queryKey }) => {
  const hotelId = queryKey[1];
  const response = await axios.get(`${API_URL}/rooms/admin/all-by-hotel/${hotelId}`);
  return response.data;
};

const HotelsManagement = () => {
  const { adminDetails } = useSelector((state) => state.admin);
  const isSuperAdmin = !adminDetails?.hotelId;
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [togglingHotelId, setTogglingHotelId] = useState(null);
  const [togglingRoomId, setTogglingRoomId] = useState(null);

  // Fetch all hotels (only if Super Admin)
  const { isLoading: loadingHotels, refetch: refetchHotels } = useQuery(
    ["hotelsAdmin"],
    fetchHotelsAdmin,
    {
      enabled: isSuperAdmin,
      onSuccess: (data) => {
        if (data?.status) {
          setHotels(data.value || []);
        } else {
          toast.error("Failed to load hotels list");
        }
      },
      onError: () => toast.error("Failed to load hotels list"),
    }
  );

  // Fetch rooms for selected hotel
  const hotelIdForRooms = selectedHotel?._id || adminDetails?.hotelId?._id || adminDetails?.hotelId;
  const { isLoading: loadingRooms, refetch: refetchRooms } = useQuery(
    ["roomsAdmin", hotelIdForRooms],
    fetchRoomsByHotelAdmin,
    {
      enabled: Boolean(hotelIdForRooms),
      onSuccess: (data) => {
        if (data?.status) {
          setRooms(data.value || []);
        } else {
          toast.error("Failed to load rooms list");
        }
      },
      onError: () => toast.error("Failed to load rooms list"),
    }
  );

  useEffect(() => {
    // If regular admin, select their hotel automatically
    if (!isSuperAdmin && adminDetails?.hotelId) {
      setSelectedHotel(adminDetails.hotelId);
    }
  }, [adminDetails, isSuperAdmin]);

  const handleToggleHotel = async (hotelId, currentStatus) => {
    setTogglingHotelId(hotelId);
    try {
      const response = await axios.post(`${API_URL}/hotels/toggle-active`, {
        id: hotelId,
        active: !currentStatus,
      });
      if (response.data.status) {
        toast.success(`Hotel ${!currentStatus ? "activated" : "deactivated"} successfully`);
        if (isSuperAdmin) {
          refetchHotels();
        }
        if (selectedHotel && selectedHotel._id === hotelId) {
          setSelectedHotel({ ...selectedHotel, active: !currentStatus });
        }
      } else {
        toast.error(response.data.errors?.[0]?.msg || "Failed to update status");
      }
    } catch (err) {
      toast.error("Failed to toggle hotel status");
    } finally {
      setTogglingHotelId(null);
    }
  };

  const handleToggleRoom = async (roomId, currentStatus) => {
    setTogglingRoomId(roomId);
    try {
      const response = await axios.post(`${API_URL}/rooms/toggle-active`, {
        id: roomId,
        active: !currentStatus,
      });
      if (response.data.status) {
        toast.success(`Room type ${!currentStatus ? "enabled" : "disabled"} successfully`);
        refetchRooms();
      } else {
        toast.error(response.data.errors?.[0]?.msg || "Failed to update room status");
      }
    } catch (err) {
      toast.error("Failed to toggle room status");
    } finally {
      setTogglingRoomId(null);
    }
  };

  const filteredHotels = hotels.filter((hotel) =>
    hotel.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    hotel.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="container-fluid animate-box">
        {/* Header Section */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800">
            {selectedHotel ? `Rooms: ${makeTitleCase(selectedHotel.name)}` : "Properties & Hotels"}
          </h1>
          {selectedHotel && isSuperAdmin && (
            <Button
              variant="outline-secondary"
              onClick={() => setSelectedHotel(null)}
              className="d-flex align-items-center"
            >
              <FaArrowLeft className="mr-2" /> Back to Hotels
            </Button>
          )}
        </div>

        {/* View 1: Super Admin Hotel List */}
        {isSuperAdmin && !selectedHotel && (
          <Card className="shadow mb-4">
            <Card.Header className="py-3 d-flex flex-row align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">All Hotel Properties</h6>
              <Form.Control
                type="text"
                placeholder="Search hotels or locations..."
                style={{ width: "300px" }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Card.Header>
            <Card.Body>
              {loadingHotels ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : filteredHotels.length === 0 ? (
                <div className="text-center py-5 text-muted">No hotels found.</div>
              ) : (
                <Table responsive hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Hotel Name</th>
                      <th>Location</th>
                      <th>Rating</th>
                      <th>Status</th>
                      <th>Room Types</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHotels.map((hotel) => (
                      <tr key={hotel._id}>
                        <td>
                          {hotel.coverImage ? (
                            <img
                              src={hotel.coverImage}
                              alt={hotel.name}
                              style={{ width: "60px", height: "45px", objectFit: "cover", borderRadius: "4px" }}
                            />
                          ) : (
                            <div className="bg-light d-flex align-items-center justify-content-center" style={{ width: "60px", height: "45px", borderRadius: "4px" }}>
                              <FaHotel className="text-muted" />
                            </div>
                          )}
                        </td>
                        <td><strong>{makeTitleCase(hotel.name)}</strong></td>
                        <td>{hotel.location}</td>
                        <td>
                          <span className="text-warning">{"★".repeat(hotel.rating || 0)}</span>
                          <span className="text-muted">{"☆".repeat(5 - (hotel.rating || 0))}</span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Form.Check
                              type="switch"
                              id={`hotel-switch-${hotel._id}`}
                              checked={hotel.active !== false}
                              disabled={togglingHotelId === hotel._id}
                              onChange={() => handleToggleHotel(hotel._id, hotel.active !== false)}
                              className="mr-2"
                              style={{ transform: "scale(1.15)", cursor: "pointer" }}
                            />
                            {hotel.active !== false ? (
                              <span className="badge badge-success px-2 py-1">Active</span>
                            ) : (
                              <span className="badge badge-danger px-2 py-1">Inactive</span>
                            )}
                          </div>
                        </td>
                        <td>{hotel.numberOfRooms || 0} Rooms</td>
                        <td>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setSelectedHotel(hotel)}
                          >
                            Manage Rooms
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        {/* View 2: Hotel Rooms / Room Types Management (Visible to Super Admin for selected hotel, or Hotel Admin) */}
        {selectedHotel && (
          <div className="row">
            {/* Hotel Status Control Header Card */}
            <div className="col-12 mb-4">
              <Card className="shadow border-left-primary">
                <Card.Body className="d-flex flex-column flex-md-row align-items-md-center justify-content-between">
                  <div>
                    <h5 className="font-weight-bold text-primary mb-1">
                      {makeTitleCase(selectedHotel.name)}
                    </h5>
                    <p className="text-muted mb-0">{selectedHotel.address}</p>
                  </div>
                  <div className="mt-3 mt-md-0 p-3 bg-light rounded d-flex align-items-center">
                    <span className="mr-3 font-weight-bold text-gray-800">
                      Overall Hotel Booking Status:
                    </span>
                    <Form.Check
                      type="switch"
                      id={`hotel-active-top-switch`}
                      checked={selectedHotel.active !== false}
                      disabled={togglingHotelId === selectedHotel._id}
                      onChange={() => handleToggleHotel(selectedHotel._id, selectedHotel.active !== false)}
                      className="mr-2"
                      style={{ transform: "scale(1.25)", cursor: "pointer" }}
                    />
                    {selectedHotel.active !== false ? (
                      <span className="badge badge-success font-weight-bold px-3 py-2">
                        <FaCheckCircle className="mr-1" /> ACTIVE & OPEN
                      </span>
                    ) : (
                      <span className="badge badge-danger font-weight-bold px-3 py-2">
                        <FaTimesCircle className="mr-1" /> FULLY CLOSED
                      </span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </div>

            {/* Room Types Listing Card */}
            <div className="col-12">
              <Card className="shadow mb-4">
                <Card.Header className="py-3">
                  <h6 className="m-0 font-weight-bold text-primary">Room Types (Availabilities)</h6>
                </Card.Header>
                <Card.Body>
                  {/* Warning banner if the hotel itself is turned off */}
                  {selectedHotel.active === false && (
                    <div className="alert alert-warning mb-4" role="alert">
                      <strong>Warning!</strong> The entire hotel is turned off. Consequently, all room types are unavailable for public booking regardless of their individual settings.
                    </div>
                  )}

                  {loadingRooms ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : rooms.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      No room types registered for this hotel.
                    </div>
                  ) : (
                    <Table responsive hover className="align-middle">
                      <thead>
                        <tr>
                          <th>Room Type</th>
                          <th>Price (Naira)</th>
                          <th>Promo Price (Naira)</th>
                          <th>Status (Active/Inactive)</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rooms.map((room) => (
                          <tr
                            key={room._id}
                            style={
                              room.active === false || selectedHotel.active === false
                                ? { opacity: 0.7 }
                                : {}
                            }
                          >
                            <td>
                              <div className="d-flex align-items-center">
                                <FaBed className="text-primary mr-2" style={{ fontSize: "1.2rem" }} />
                                <strong>{makeTitleCase(room.name)}</strong>
                              </div>
                            </td>
                            <td>₦{room.priceNgn?.toLocaleString() || "0"}</td>
                            <td>
                              {room.promoPriceNgn > 0 ? (
                                <span className="text-success">₦{room.promoPriceNgn?.toLocaleString()}</span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Form.Check
                                  type="switch"
                                  id={`room-switch-${room._id}`}
                                  checked={room.active !== false}
                                  disabled={togglingRoomId === room._id || selectedHotel.active === false}
                                  onChange={() => handleToggleRoom(room._id, room.active !== false)}
                                  className="mr-2"
                                  style={{ transform: "scale(1.15)", cursor: selectedHotel.active === false ? "not-allowed" : "pointer" }}
                                />
                                {room.active !== false ? (
                                  <span className="badge badge-success px-2 py-1">Available</span>
                                ) : (
                                  <span className="badge badge-danger px-2 py-1">Unavailable</span>
                                )}
                              </div>
                            </td>
                            <td className="text-muted" style={{ maxWidth: "300px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                              {room.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default HotelsManagement;
